// FileUploadContext.tsx
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { v4 as uuidv4 } from 'uuid';
import { useLazyGetUploadParametersQuery } from '../features/Admin/adminSlice';
import { HttpClient } from '../lib/HttpClient';

type FileUploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

interface IProgress {
  totalBytes: number;
  doneBytes: number;
  donePercent: number;
}

export interface FileUpload {
  id: string;
  file: File;
  progress: IProgress;
  status: FileUploadStatus
}

interface FileUploadContextValue {
  uploads: FileUpload[];
  addUpload: (eventId: string, file: File, overlayKey?: string) => Promise<void>;
}

interface FileUploadRequest {
  formData: FormData;
  url: string;
}

const FileUploadContext = createContext<FileUploadContextValue | undefined>(undefined);
const httpClient = new HttpClient();

const uploadsStore: FileUpload[] = [];

const addUploadToStore = (upload: FileUpload): void => {
  uploadsStore.push(upload);
  uploadsMutatedCallback();
}


const removeUploadFromStore = (id: string): void => {
  const index = uploadsStore.findIndex(x => x.id === id);
  if (index >= 0) {
    uploadsStore.splice(index, 1);
    uploadsMutatedCallback();
  }
}

const updateUploadInStore = (id: string, callback: (upload: FileUpload) => boolean) => {
  console.info(`Updating upload data for '${id}', number of uploads: ${uploadsStore.length}`)
  // get the index of the FileUpload based on the filename
  const index = uploadsStore.findIndex(x => {
    console.info(`${x.id} === ${id} -> ${x.id === id}`)
    return x.id === id
  });
  if (index >= 0) {
    console.info(`Before updating upload: ${JSON.stringify(uploadsStore[index], null, 2)}`)
    // Invoke the callback so that calling code can update the FileUpload instance
    if (callback(uploadsStore[index])) {
      console.info(`After updating upload: ${JSON.stringify(uploadsStore[index], null, 2)}`)

      // Generate a new array of the uploads (to trigger a render) and assign it
      uploadsMutatedCallback();
    }
  } else {
    console.warn(`Did not find upload item for '${id}'`)
  }
}

const emptyStoreIfNoPendingOrOngoingUploads = (): void => {
  if (uploadsStore.filter(x => x.status === 'pending' || x.status === 'uploading').length === 0) {
    const items = [...uploadsStore];
    // Wait a little time, then remove the items
    setTimeout(() => {
      for (const upload of items) {
        removeUploadFromStore(upload.id);
        uploadsMutatedCallback();
      }
    }, 3000)
  }
}

let uploadsMutatedCallback: () => void = () => { };

export const FileUploadProvider = ({ children }: { children: ReactNode }) => {


  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [getUploadParameters] = useLazyGetUploadParametersQuery({});
  const auth = useAuth();


  const refreshUploadsState = () => {
    setUploads([...uploadsStore]);
    emptyStoreIfNoPendingOrOngoingUploads();
  }

  uploadsMutatedCallback = refreshUploadsState;


  useEffect(() => {
    console.info(`Current uploads: ${JSON.stringify(uploads.map(x => {
      return {
        id: x.id,
        file: x.file.name,
        progress: x.progress,
        status: x.status
      }
    }), null, 2)}`)
  }, [uploads])


  // Gets an upload request for the given event and file
  const getUploadRequest = async (eventId: string, file: File, overlayKey?: string): Promise<FileUploadRequest | undefined> => {
    console.info(`Getting upload request data for '${file.name}'`)
    // Get upload parameters from the backend
    const response = await getUploadParameters({ eventId: eventId, filename: file.name, accessToken: auth.user?.access_token ?? '', overlayKey });
    if (response.isSuccess) {
      const uploadParameters = response.data;
      const formdata = new FormData();

      // Put the fields from the upload parameters into the form data
      for (const field of Object.keys(uploadParameters.fields)) {
        formdata.append(field, uploadParameters.fields[field]);
      }
      //...and add the file
      formdata.append('file', file);

      const result = { formData: formdata, url: uploadParameters.url };
      console.info(`Upload request for '${file.name}: ${JSON.stringify(result)}'`)
      return result
    }
    return undefined;
  }

  const handleUploadProgress = (id: string, event: ProgressEvent, latestProgressUpdateTime: Date) => {
    // console.info(`Upload progress for '${filename}': ${event.loaded}`)
    updateUploadInStore(id, (upload) => {
      if (upload.status !== 'uploading') {
        upload.status = 'uploading';
        // Signal that an update was made
        return true;
      }

      // Signal that no update was made
      return false;
    });

    const now = new Date();
    if (now.valueOf() - latestProgressUpdateTime.valueOf() > 200) {

      const progressInPercent = Math.round(event.loaded / event.total * 100);

      updateUploadInStore(id, (upload) => {
        upload.progress = {
          totalBytes: event.total,
          doneBytes: event.loaded,
          donePercent: progressInPercent
        };
        return true;
      });
      return now;
    }
    else {
      return latestProgressUpdateTime;
    }
  }

  const addUpload = async (eventId: string, file: File, overlayKey?: string): Promise<void> => {
    const uploadId = uuidv4();
    const newUpload: FileUpload = { id: uploadId, file, progress: { totalBytes: file.size, doneBytes: 0, donePercent: 0 }, status: 'pending' };

    addUploadToStore(newUpload);

    const uploadRequest = await getUploadRequest(eventId, file, overlayKey);
    if (uploadRequest !== undefined) {

      let latestProgressUpdate: Date = new Date((new Date().valueOf() - 1000));

      await httpClient.post(uploadRequest.url, uploadRequest.formData, (event: ProgressEvent) => {
        latestProgressUpdate = handleUploadProgress(uploadId, event, latestProgressUpdate)
      });

      updateUploadInStore(uploadId, (upload) => {
        upload.progress = { ...upload.progress, doneBytes: upload.progress.totalBytes, donePercent: 100 };
        upload.status = 'completed';
        return true;
      });
    }
  }


  return (
    <FileUploadContext.Provider value={{ uploads, addUpload }}>
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = () => {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error('useFileUpload must be used within a FileUploadProvider');
  }
  return context;
};
